<template>
    <form-fields>
        <form-fields-main-scroll>
            <toolbar-buttons :justify="true" :important="true">
                <icon-button
                    :label="$t('V3_ADD_TEXT')"
                    :selected="flow === 'text'"
                    :important="true"
                    @click="doText"
                >
                    <img src="../../assets/v2/layout-text.svg" />
                </icon-button>

                <icon-button
                    :label="$t('V3_ADD_MONOGRAM')"
                    :selected="flow === 'monogram'"
                    :important="true"
                    @click="doMonogram"
                >
                    <img src="../../assets/v2/layout-monogram.svg" />
                </icon-button>

                <icon-button
                    :label="$t('V3_UPLOAD')"
                    :selected="flow === 'upload'"
                    :important="true"
                    @click="doUpload"
                >
                    <img src="../../assets/v2/layout-upload.svg" />
                </icon-button>

                <icon-button
                    :label="$t('V3_GRAPHIC_GALLERY')"
                    :selected="flow === 'gallery'"
                    :important="true"
                    @click="doGallery"
                >
                    <img src="../../assets/v2/layout-gallery.svg" />
                </icon-button>
            </toolbar-buttons>
        </form-fields-main-scroll>

        <step-buttons>
            <secondary-button
                class="cz3__flex-1"
                :label="$t('V3_BACK')"
                @click="back"
            />

            <skip-button
                v-if="canSkip"
                class="cz3__flex-1"
                :label="$t('V3_SKIP')"
                @click="skip"
            />

            <action-button
                class="cz3__flex-1"
                :label="$t('V3_NEXT')"
                :disabled="flow == null"
                @click="next"
            />
        </step-buttons>
    </form-fields>
</template>

<style lang="scss">
</style>

<script>
    import ToolbarButtons from './ToolbarButtons.vue';
    import IconButton from './IconButton.vue';
    import FormFields from './FormFields.vue';
    import FormFieldsMainScroll from './FormFieldsMainScroll.vue';
    import ActionButton from './ActionButton.vue';
    import SecondaryButton from './SecondaryButton.vue';
    import SkipButton from './SkipButton.vue';
    import StepButtons from './StepButtons.vue';

    import * as constants from '../../constants';

    export default {
        components: {
            ToolbarButtons,
            IconButton,
            FormFields,
            ActionButton,
            SecondaryButton,
            SkipButton,
            StepButtons,
            FormFieldsMainScroll,
        },

        props: {
            designOn: String,
            surfaceIndex: Number,
        },

        data() {
            return {
                flow: null,
                startFlow: null,

                lastEditPlacement: null,
            };
        },

        computed: {
            nextStep() {
                return this.$store.getters.nextStepV3;
            },

            nextStepWithSkip() {
                return this.$store.getters.nextStepWithSkipV3;
            },

            prevStep() {
                return this.$store.getters.prevStepV3;
            },

            personalizationMethod() {
                return this.$store.state.customizer.personalizationMethod;
            },

            selectedStep() {
                return this.$store.state.selectedStepV3;
            },

            canSkip() {
                if (this.personalizationMethod === 'printing') {
                    // If on printing, and we are on the second side, can be skipped only if there are already any personalizations.
                    if (this.selectedStep === 'design-2') {
                        return this.filledPlacements.length > 0;
                    }

                    return true;
                }

                if (this.personalizationMethod === 'etching') {
                    return true;
                }

                return false;
            },

            filledPlacements() {
                return this.$store.getters.filledPlacements;
            },
        },

        mounted() {
            const allOnThisSide = this.$store.getters.filledPlacements.filter((x) => x.component.custom['v-design-on'] === this.designOn);

            const design = allOnThisSide[this.surfaceIndex];

            if (design) {
                if (design.placement.code.startsWith(constants.PLACEMENT_IMAGE)) {
                    if (design.component.custom['gallery-image'] === 'true') {
                        this.flow = 'gallery';
                        this.lastEditPlacement = design;
                    } else {
                        this.flow = 'upload';
                        this.lastEditPlacement = design;
                    }
                }

                if (design.placement.code.startsWith(constants.PLACEMENT_TEXT)) {
                    this.flow = 'text';
                    this.lastEditPlacement = design;
                }

                if (design.placement.code.startsWith(constants.PLACEMENT_MONOGRAM)) {
                    this.flow = 'monogram';
                    this.lastEditPlacement = design;
                }
            }

            if (this.lastEditPlacement) {
                this.$store.dispatch('editPlacement', this.lastEditPlacement.placement);
            }

            this.startFlow = this.flow;
        },

        methods: {
            doGallery() {
                this.selectOption('gallery');
            },

            doUpload() {
                this.selectOption('upload');
            },

            doText() {
                this.selectOption('text');
            },

            doMonogram() {
                this.selectOption('monogram');
            },

            selectOption(option) {
                this.flow = option;
            },

            back() {
                this.$store.dispatch('setSelectedStepV3', this.prevStep);
            },

            skip() {
                if (this.lastEditPlacement) {
                    this.$store.dispatch('showGenericConfirm', {
                        title: this.$t('V3_CHANGE_DESIGN_CONFIRM'),
                        description: this.$t('V3_CHANGE_DESIGN_BODY'),
                        cancelAction: this.$t('V3_BACK'),
                        confirmAction: this.$t('V3_CHANGE'),
                        confirmationCallback: () => {
                            this.$store.dispatch('clearPlacement', this.lastEditPlacement);
                            this.$store.dispatch('editPlacement', null);

                            this.$store.dispatch('setSelectedStepV3', this.nextStepWithSkip);
                        },
                    });

                    return;
                }

                this.$store.dispatch('setSelectedStepV3', this.nextStepWithSkip);
            },

            next() {
                if (this.lastEditPlacement) {
                    if (this.flow !== this.startFlow) {
                        this.$store.dispatch('showGenericConfirm', {
                            title: this.$t('V3_CHANGE_DESIGN_CONFIRM'),
                            description: this.$t('V3_CHANGE_DESIGN_BODY'),
                            cancelAction: this.$t('V3_BACK'),
                            confirmAction: this.$t('V3_CHANGE'),
                            confirmationCallback: () => {
                                this.$store.dispatch('clearPlacement', this.lastEditPlacement);
                                this.$store.dispatch('editPlacement', null);

                                this.$store.dispatch('setSelectedSubStepV3', this.flow);

                                if (this.flow === 'gallery') {
                                    this.$store.dispatch('showImageGalleryV3', true);
                                }
                            },
                        });

                        return;
                    }

                    this.$store.dispatch('editPlacement', this.lastEditPlacement.placement);
                }

                this.$store.dispatch('setSelectedSubStepV3', this.flow);

                if (this.flow === 'gallery') {
                    this.$store.dispatch('showImageGalleryV3', true);
                }
            },
        },
    };
</script>
